import React, { useState } from 'react';
import { FileText, Download, Calendar } from 'lucide-react';
import { useGenerateReport, useEvaluation, useReportHistory } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner, Modal } from '@/components/UI';
import { formatDateTime, formatDate } from '@/utils';

export function ReportsPage() {
  const generateReport = useGenerateReport();
  const [showGenerate, setShowGenerate] = useState(false);
  const [reportType, setReportType] = useState('daily');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEval, setSelectedEval] = useState<string | null>(null);

  const { data: evalData, isLoading: evalLoading } = useEvaluation(selectedEval || '');
  const { data: history, isLoading: histLoading } = useReportHistory();

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await generateReport.mutateAsync({
        type: reportType,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        format: 'json',
      });
      setShowGenerate(false);
    } catch { /* handled */ }
  };

  const reportTypes = [
    { value: 'daily', label: 'Daily Report' },
    { value: 'weekly', label: 'Weekly Report' },
    { value: 'monthly', label: 'Monthly Report' },
    { value: 'evaluation_7d', label: '7-Day Evaluation' },
    { value: 'evaluation_30d', label: '30-Day Evaluation' },
  ];

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-surface-100">Reports</h1>
        <button onClick={() => setShowGenerate(true)} className="btn-primary btn-sm">
          <FileText className="w-4 h-4" /> Generate Report
        </button>
      </div>

      {/* Quick Evaluation Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card
          title="7-Day Evaluation"
          action={
            <button onClick={() => setSelectedEval('7d')} className="btn-ghost btn-sm">
              View
            </button>
          }
        >
          <p className="text-sm text-surface-400">
            Evaluate trading performance over the past 7 days with detailed metrics and recommendations.
          </p>
        </Card>
        <Card
          title="30-Day Evaluation"
          action={
            <button onClick={() => setSelectedEval('30d')} className="btn-ghost btn-sm">
              View
            </button>
          }
        >
          <p className="text-sm text-surface-400">
            Comprehensive 30-day evaluation with trend analysis, strategy effectiveness, and risk assessment.
          </p>
        </Card>
      </div>

      {/* Evaluation Detail */}
      {selectedEval && evalData && (
        <Card title={`${selectedEval === '7d' ? '7-Day' : '30-Day'} Evaluation Result`}>
          {evalLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-surface-300 leading-relaxed">
                {String(evalData.summary ?? 'No summary available')}
              </p>
              {evalData.metrics && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(evalData.metrics as Record<string, number>).map(([key, value]) => (
                    <div key={key} className="p-3 rounded-lg bg-surface-800/50">
                      <p className="text-xs text-surface-500 capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-medium text-surface-200">
                        {typeof value === 'number' ? value.toFixed(4) : String(value)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Report History */}
      <Card title="Report History">
        {histLoading ? (
          <LoadingSpinner />
        ) : !history || (history as unknown[]).length === 0 ? (
          <p className="text-sm text-surface-500 text-center py-8">No reports generated yet</p>
        ) : (
          <div className="space-y-2">
            {(history as Record<string, unknown>[]).map((report: Record<string, unknown>, i: number) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-surface-500" />
                  <div>
                    <p className="text-sm text-surface-200">{String(report.title ?? 'Untitled Report')}</p>
                    <p className="text-xs text-surface-500">
                      {formatDate(String(report.period_start ?? ''))} — {formatDate(String(report.period_end ?? ''))}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="brand">{String(report.type ?? '')}</Badge>
                  <span className="text-xs text-surface-600">{formatDateTime(String(report.created_at ?? ''))}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Generate Modal */}
      <Modal isOpen={showGenerate} onClose={() => setShowGenerate(false)} title="Generate Report" size="md">
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">Report Type</label>
            <select className="select" value={reportType} onChange={(e) => setReportType(e.target.value)}>
              {reportTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">Start Date</label>
              <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">End Date</label>
              <input type="date" className="input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowGenerate(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={generateReport.isPending}>
              {generateReport.isPending ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
